import React from 'react';

const _inlineCodeStyle = {
  backgroundColor: '#f0f0f0',
  padding: '1px 4px',
  borderRadius: '3px',
  fontFamily: 'monospace',
  fontSize: '0.9em',
};

const _linkStyle = { color: '#2563eb', textDecoration: 'underline', cursor: 'pointer' };

// A root-relative path ("/x") stays in the app — but NOT protocol-relative
// "//host", which is a DIFFERENT origin. Guarding the "//" matters: a bare
// startsWith('/') would short-circuit the origin check and treat //evil.com as
// same-origin (!481 review).
function _isRootRelative(href) {
  return href.startsWith('/') && !href.startsWith('//');
}

// !481 review: link hrefs carry LLM-influenced text (any assistant message can
// contain a markdown link), so allowlist the scheme BEFORE rendering an <a> at
// all — root-relative, or an http/https URL. Anything else (javascript:, data:,
// mailto:, …) is refused and the markdown is shown as plain text, so
// `[x](javascript:…)` can never become a clickable XSS sink.
function _isSafeHref(href) {
  if (_isRootRelative(href)) return true;
  try {
    const { protocol } = new URL(href, window.location.origin);
    return protocol === 'http:' || protocol === 'https:';
  } catch (e) {
    return false;
  }
}

// station-chat-files: a link to a chat attachment is a DOWNLOAD, not navigation.
// The `download` attribute makes a same-origin URL download WITHOUT a top-level
// navigation, so clicking a file never unloads the station page. Matched narrowly
// against the BFF's own route shape so this can't turn an arbitrary in-app link
// into a download.
const _FILE_DOWNLOAD_RE = /^\/api\/files\/\d+\/[0-9a-f]{64}$/;

function _isFileDownload(href) {
  return _isRootRelative(href) && _FILE_DOWNLOAD_RE.test(href);
}

// save-gallery-link: a same-origin link stays inside the app — those are the ones
// the leave-guard (`onNavigate`) intercepts; external links open in a new tab.
function _isInternalHref(href) {
  if (_isRootRelative(href)) return true;
  try {
    return new URL(href, window.location.origin).origin === window.location.origin;
  } catch (e) {
    return false;
  }
}

// chat-autolink: a bare http(s) URL in chat text becomes a link. The scheme is part of the
// match, so `javascript:`/`data:` can never start one; the href still goes through _isSafeHref.
// No scheme-less `www.x` (file names, false positives).
const _BARE_URL_SRC = String.raw`\bhttps?:\/\/[^\s<>"'\x60]+`;
const _BARE_URL_RE = new RegExp(_BARE_URL_SRC, 'g');

// Prose wraps a URL in punctuation that is not part of it: "see https://x.com/a." or
// "(https://x.com/a)". Trim trailing sentence punctuation, and a closing ) or ] only when
// it is unbalanced inside the URL — Wikipedia-style "/Foo_(bar)" keeps its ")".
function _trimUrl(url) {
  let u = url;
  for (;;) {
    const last = u[u.length - 1];
    if ('.,;:!?\'"'.includes(last)) { u = u.slice(0, -1); continue; }
    if (last === ')' && (u.split('(').length - 1) < (u.split(')').length - 1)) { u = u.slice(0, -1); continue; }
    if (last === ']' && (u.split('[').length - 1) < (u.split(']').length - 1)) { u = u.slice(0, -1); continue; }
    return u;
  }
}

// The link to emit for a raw URL match, or null when it isn't one (e.g. "https://.").
function _bareUrl(raw) {
  const url = _trimUrl(raw);
  return /^https?:\/\/./.test(url) && _isSafeHref(url) ? url : null;
}

// Text → parts with its bare URLs as links. For emphasis content: the inline parser
// claims `**…**`/`*…*` as a unit, so a URL inside one is only found here.
function _linkifyText(content) {
  const parts = [];
  _BARE_URL_RE.lastIndex = 0;                      // module-level /g regex
  let last = 0;
  let m;
  while ((m = _BARE_URL_RE.exec(content)) !== null) {
    const url = _bareUrl(m[0]);
    if (!url) continue;
    if (m.index > last) parts.push({ type: 'text', content: content.slice(last, m.index) });
    parts.push({ type: 'link', content: url, href: url });
    last = m.index + url.length;
    _BARE_URL_RE.lastIndex = last;                 // resume after the trimmed URL
  }
  if (last < content.length) parts.push({ type: 'text', content: content.slice(last) });
  return parts;
}

// Image alternative FIRST (its `![` prefix would otherwise parse as a link preceded by a
// literal `!`), then links, then the emphasis rules. Chat adds a bare-URL alternative so a
// URL is claimed whole before a `*` inside it can start emphasis.
const _INLINE_SRC = String.raw`!\[([^\]]*)\]\(([^)\s]+)\)|\[([^\]]+)\]\(([^)\s]+)\)|\*\*(.+?)\*\*|\*(.+?)\*|\x60(.+?)\x60`;
const _INLINE_RE = new RegExp(`(${_INLINE_SRC})`, 'g');
const _CHAT_INLINE_RE = new RegExp(`(${_INLINE_SRC}|(${_BARE_URL_SRC}))`, 'g');

/**
 * Inline markdown within a single (non-code-block) text segment:
 * [text](href) links, **bold**, *italic*, `code`.
 *
 * save-gallery-link: `onNavigate(href)`, when provided, intercepts clicks on
 * INTERNAL links (e.g. the "save this" → /gallery reply) so the host can route
 * them through the station leave-guard instead of navigating directly.
 */
function renderInline(text, keyBase, onNavigate, chat = false) {
  const parts = [];
  const regex = chat ? _CHAT_INLINE_RE : _INLINE_RE;
  regex.lastIndex = 0;                             // module-level /g regex
  const pushText = (content) => { if (content) parts.push({ type: 'text', content }); };
  const emphasis = (type, content) =>
    parts.push(chat ? { type, children: _linkifyText(content) } : { type, content });
  let lastIndex = 0;
  let match;

  while ((match = regex.exec(text)) !== null) {
    if (match.index > lastIndex) pushText(text.slice(lastIndex, match.index));
    lastIndex = regex.lastIndex;
    if (match[3] !== undefined) {
      // ![alt](src) — tobin-ordering (!512): product images in chat.
      // SAME-ORIGIN only (root-relative src): unlike links, an <img> fires
      // its request without a click, so an external URL in any assistant-
      // rendered markdown is a tracking beacon (!512 review #2). Non-root-
      // relative srcs render as literal markdown, never an <img>.
      if (_isRootRelative(match[3])) parts.push({ type: 'image', alt: match[2] || '', src: match[3] });
      else parts.push({ type: 'text', content: match[1] });
    }
    else if (match[5] !== undefined) {
      // Only a scheme-safe href becomes a link; otherwise render the literal
      // markdown text (match[1] = the whole `[text](href)`), never an anchor.
      if (_isSafeHref(match[5])) parts.push({ type: 'link', content: match[4], href: match[5] });
      else parts.push({ type: 'text', content: match[1] });
    }
    else if (match[6]) emphasis('bold', match[6]);
    else if (match[7]) emphasis('italic', match[7]);
    else if (match[8]) parts.push({ type: 'code', content: match[8] });
    else if (match[9]) {
      const url = _bareUrl(match[9]);
      if (url) {
        parts.push({ type: 'link', content: url, href: url });
        lastIndex = match.index + url.length;      // trimmed punctuation goes back to the text
        regex.lastIndex = lastIndex;
      } else pushText(match[9]);
    }
  }
  pushText(text.slice(lastIndex));

  const renderPart = (part, key) => {
    switch (part.type) {
      case 'image':
        return (
          <img key={key} src={part.src} alt={part.alt}
               style={{ display: 'block', maxWidth: '100%', maxHeight: '180px',
                        borderRadius: '8px', margin: '4px 0', objectFit: 'cover' }}
               onError={(e) => { e.currentTarget.style.display = 'none'; }} />
        );
      case 'link': {
        const internal = _isInternalHref(part.href);
        // A chat-attachment link downloads in place: no new tab, no navigation,
        // and NOT routed through onNavigate (the leave-guard exists for links
        // that unload the station page — a download doesn't).
        if (_isFileDownload(part.href)) {
          return (
            <a key={key} href={part.href} style={_linkStyle} download>
              {part.content}
            </a>
          );
        }
        // chat-autolink: in a chat bubble EVERY link opens in a new tab — internal ones too.
        // A new tab never unloads the station page, so there is nothing for the leave-guard
        // (`onNavigate`) to protect; outside chat (cards, documents) the old rule stands.
        const newTab = chat || !internal;
        return (
          <a key={key} href={part.href} style={_linkStyle}
             {...(newTab ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
             onClick={(e) => {
               if (!newTab && onNavigate && internal) { e.preventDefault(); onNavigate(part.href); }
             }}>
            {part.content}
          </a>
        );
      }
      case 'bold':
        return <strong key={key}>{part.children ? part.children.map((c, j) => renderPart(c, `${key}-${j}`)) : part.content}</strong>;
      case 'italic':
        return <em key={key}>{part.children ? part.children.map((c, j) => renderPart(c, `${key}-${j}`)) : part.content}</em>;
      case 'code':
        return <code key={key} style={_inlineCodeStyle}>{part.content}</code>;
      default:
        return <React.Fragment key={key}>{part.content}</React.Fragment>;
    }
  };
  return parts.map((part, i) => renderPart(part, `${keyBase}-${i}`));
}

// chat-table-markdown: GFM pipe-table support. The renderer is hand-rolled (no
// markdown lib — deliberate, for the href/img allowlisting above), so tables were
// never handled: `| a | b |` / `|---|---|` rows fell through to renderInline and
// showed as literal pipes/dashes (the outer chat bubble is `white-space: pre-wrap`,
// so they stacked as raw lines). These parse a table block out of a text run and
// render a real <table>; cells go through renderInline so bold/links/code inside a
// cell (and their security allowlisting) still apply.

const _tdBase = { border: '1px solid #d0d7de', padding: '4px 8px' };

// Split one table line into trimmed cells. Strips one optional leading/trailing
// pipe (GFM allows them to be absent), and honors `\|` escapes inside a cell.
function _splitRow(line) {
  let s = line.trim();
  if (s.startsWith('|')) s = s.slice(1);
  if (s.endsWith('|')) s = s.slice(0, -1);
  const cells = [];
  let cur = '';
  for (let i = 0; i < s.length; i += 1) {
    if (s[i] === '\\' && s[i + 1] === '|') { cur += '|'; i += 1; }
    else if (s[i] === '|') { cells.push(cur.trim()); cur = ''; }
    else cur += s[i];
  }
  cells.push(cur.trim());
  return cells;
}

// The delimiter row: every cell is dashes with optional leading/trailing colons
// (`:--`, `--:`, `:-:`). A pipe is required (so a bare `---` horizontal rule under
// a line of text is NOT mistaken for a 1-column table).
function _isDelimiterRow(line) {
  if (!line.includes('|') || !line.includes('-')) return false;
  const cells = _splitRow(line);
  return cells.length > 0 && cells.every((c) => /^:?-+:?$/.test(c));
}

function _cellAlign(spec) {
  const l = spec.startsWith(':');
  const r = spec.endsWith(':');
  if (l && r) return 'center';
  if (r) return 'right';
  if (l) return 'left';
  return null;
}

// company-page: DOCUMENT-MODE blocks — headings, bullet/numbered lists and
// paragraphs. Opt-in via the `blocks` prop and OFF everywhere else on purpose:
// chat bubbles are `white-space: pre-wrap` and every line break in an assistant
// reply is already meaningful, so turning `- x` into an <li> or `# x` into an
// <h1> mid-conversation would restyle a thousand existing messages. A prose PAGE
// (the company page) wants the opposite, hence the switch rather than a default.
//
// WHAT THIS DOES NOT DO, written down because the last two gaps here were found
// by looking at a rendered page rather than by reading the code (!591 review):
// indentation is ignored, so a nested list flattens to one level; `>` blockquotes
// and `---` rules fall through to renderInline and show as literal characters.
// Deliberate — this is a hand-rolled parser kept small on purpose (the href/img
// allowlisting below is why there's no markdown library), and the company page
// needs none of them. Adding one means adding its test.
const _H = /^(#{1,6})\s+(.*)$/;
const _LI = /^\s*([-*+]|\d+[.)])\s+(.+)$/;

function _parseDocBlocks(text) {
  const out = [];
  const lines = text.split('\n');
  let para = [];
  let list = null;   // { ordered, items: [] }
  const flushPara = () => {
    if (para.length) { out.push({ type: 'para', content: para.join('\n') }); para = []; }
  };
  const flushList = () => {
    if (list) { out.push({ type: 'list', ...list }); list = null; }
  };
  for (const line of lines) {
    const h = _H.exec(line);
    if (h) {
      flushPara(); flushList();
      out.push({ type: 'heading', level: h[1].length, content: h[2].trim() });
      continue;
    }
    const li = _LI.exec(line);
    if (li) {
      flushPara();
      const ordered = /\d/.test(li[1]);
      // A bullet run and a numbered run are separate lists; switching marker
      // type starts a new one rather than mixing <ul> and <ol> items.
      if (!list || list.ordered !== ordered) { flushList(); list = { ordered, items: [] }; }
      list.items.push(li[2].trim());
      continue;
    }
    if (line.trim() === '') { flushPara(); flushList(); continue; }
    if (list) {
      // Lazy continuation: a hard-wrapped list item. Markdown authors wrap at
      // 80 columns and indent the runover, and treating that second line as a
      // new paragraph splits every wrapped bullet in half — which is exactly
      // how the first company page rendered.
      list.items[list.items.length - 1] += ` ${line.trim()}`;
      continue;
    }
    para.push(line);
  }
  flushPara(); flushList();
  return out;
}

const _headingStyle = (level) => ({
  fontSize: [null, '1.6em', '1.25em', '1.05em', '1em', '1em', '1em'][level],
  fontWeight: 600,
  lineHeight: 1.25,
  // Every heading carries a top margin. Cancelling it on the FIRST block is the
  // page's job (`.company-body > :first-child { margin-top: 0 }`) — this
  // renderer doesn't know where in a page it sits.
  margin: level <= 2 ? '1.4em 0 0.5em' : '1.2em 0 0.4em',
});

function _renderDocSegment(seg, key, onNavigate, chat = false) {
  if (seg.type === 'heading') {
    const Tag = `h${Math.min(seg.level, 6)}`;
    return React.createElement(
      Tag, { key, style: _headingStyle(seg.level) },
      renderInline(seg.content, `${key}-h`, onNavigate, chat));
  }
  if (seg.type === 'list') {
    const Tag = seg.ordered ? 'ol' : 'ul';
    return React.createElement(
      // listStyle is explicit: index.css resets it, so an <ul> without this
      // renders as an indented run of unmarked lines.
      Tag, { key, style: { margin: '0.5em 0', paddingLeft: '1.4em',
                           listStyle: seg.ordered ? 'decimal' : 'disc' } },
      seg.items.map((it, i) => (
        <li key={i} style={{ margin: '0.25em 0' }}>
          {renderInline(it, `${key}-li-${i}`, onNavigate, chat)}
        </li>
      )));
  }
  return (
    <p key={key} style={{ margin: '0.7em 0', lineHeight: 1.6 }}>
      {renderInline(seg.content, `${key}-p`, onNavigate, chat)}
    </p>
  );
}

// Walk a (non-code) text block into a list of segments: {type:'text'} runs and
// {type:'table'} blocks. A table = a pipe-bearing header line immediately followed
// by a matching-width delimiter row, then consecutive pipe-bearing rows.
//
// In document mode (`doc`), the non-table runs are further split into headings,
// lists and paragraphs instead of being emitted as one pre-wrapped text run.
function _parseTextBlock(text, doc = false) {
  const lines = text.split('\n');
  const segments = [];
  let buf = [];
  const flush = () => {
    if (!buf.length) return;
    const content = buf.join('\n');
    buf = [];
    if (doc) segments.push(..._parseDocBlocks(content));
    else segments.push({ type: 'text', content });
  };
  let i = 0;
  while (i < lines.length) {
    const line = lines[i];
    const next = lines[i + 1];
    if (next !== undefined && line.includes('|') && _isDelimiterRow(next)) {
      const header = _splitRow(line);
      const aligns = _splitRow(next).map(_cellAlign);
      if (header.length === aligns.length) {
        flush();
        const rows = [];
        let j = i + 2;
        while (j < lines.length && lines[j].includes('|') && lines[j].trim() !== '') {
          rows.push(_splitRow(lines[j]));
          j += 1;
        }
        segments.push({ type: 'table', header, aligns, rows });
        i = j;
        continue;
      }
    }
    buf.push(line);
    i += 1;
  }
  flush();
  return segments;
}

function _renderTable(seg, key, onNavigate, chat = false) {
  return (
    // whiteSpace:normal — the outer bubble is pre-wrap; let cell text wrap normally.
    // overflowX:auto — a wide table scrolls inside the bubble instead of overflowing.
    // overflowWrap:normal (chat-long-token-wrap, !770 review) — the chat bubbles set
    // `anywhere`, which is INHERITED and drops every cell's min-content to ~1 char: a
    // wide table then squeezed to the bubble and split words mid-letter instead of
    // scrolling (390px: table 368→268px, 15 cells split). Resetting it here restores
    // word-width columns, so the table is wider than the bubble and this div scrolls.
    <div key={key} style={{ overflowX: 'auto', margin: '6px 0', whiteSpace: 'normal', overflowWrap: 'normal' }}>
      <table style={{ borderCollapse: 'collapse', fontSize: '0.9em' }}>
        <thead>
          <tr>
            {seg.header.map((h, ci) => (
              <th key={ci} style={{ ..._tdBase, background: '#f6f8fa', fontWeight: 600,
                textAlign: seg.aligns[ci] || 'left' }}>
                {renderInline(h, `${key}-th-${ci}`, onNavigate, chat)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {seg.rows.map((row, ri) => (
            <tr key={ri}>
              {/* normalize to the header's column count: pad missing, ignore extra */}
              {seg.header.map((_h, ci) => (
                <td key={ci} style={{ ..._tdBase, textAlign: seg.aligns[ci] || 'left' }}>
                  {renderInline(row[ci] || '', `${key}-td-${ri}-${ci}`, onNavigate, chat)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

const _preStyle = {
  backgroundColor: '#f0f0f0',
  // Right padding keeps the first line clear of the copy button. It does not
  // help a line long enough to scroll — that text passes UNDER the button —
  // but the alternative is a header bar above the block, which adds height to
  // every code block in every chat bubble. Opaque button, clear resting state,
  // overlap only while dragging horizontally: the least bad of the three.
  padding: '8px 62px 8px 10px',
  borderRadius: '6px',
  fontFamily: 'monospace',
  fontSize: '0.85em',
  overflowX: 'auto',
  whiteSpace: 'pre',
  margin: 0,
};

/**
 * Put `text` on the clipboard. Resolves true on success.
 *
 * Two paths, for the same reason AuthHeader's share-link copy has two: the
 * async Clipboard API only exists in a SECURE context, and the dev stack is
 * routinely reached over plain http on a LAN address. `execCommand` is
 * deprecated and still the only thing that works there, so it stays as the
 * floor rather than as the preference.
 */
export async function copyText(text) {
  try {
    if (navigator.clipboard && window.isSecureContext) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch (e) { /* fall through to the legacy path */ }
  try {
    const ta = document.createElement('textarea');
    ta.value = text;
    // Off-screen rather than hidden: `display:none` and `visibility:hidden`
    // both make the selection un-copyable, and a focused element that scrolls
    // the page is worse than one that is merely invisible.
    ta.style.position = 'fixed';
    ta.style.top = '-1000px';
    ta.setAttribute('readonly', '');
    document.body.appendChild(ta);
    ta.select();
    const ok = document.execCommand('copy');
    document.body.removeChild(ta);
    return ok;
  } catch (e) {
    return false;
  }
}

/**
 * A fenced code block with a copy button.
 *
 * The button is ABSOLUTELY POSITIONED over the block rather than sitting above
 * it in flow, so adding it does not change the height of a single chat bubble.
 * Chat messages are laid out around their own height and the transcript scrolls
 * to the bottom on every append; a control that added 24px to every code block
 * would shift the whole conversation.
 *
 * There is no streaming case to handle. `TypingEffect` renders MarkdownText
 * over a growing prefix, but the fence regex needs its closing ``` — so an
 * unfinished block is still plain text, and this component only ever exists
 * once the content is complete. A half-copied snippet is not reachable.
 */
function CodeBlock({ content }) {
  const [state, setState] = React.useState('idle');   // idle | copied | failed

  React.useEffect(() => {
    if (state === 'idle') return undefined;
    const t = setTimeout(() => setState('idle'), 1600);
    return () => clearTimeout(t);
  }, [state]);

  const onCopy = async () => setState(await copyText(content) ? 'copied' : 'failed');

  const label = state === 'copied' ? 'Copied' : state === 'failed' ? 'Press ⌘C' : 'Copy';
  return (
    <div style={{ position: 'relative', margin: '6px 0' }}>
      <pre style={_preStyle}><code>{content}</code></pre>
      <button
        type="button"
        onClick={onCopy}
        // The accessible name says WHAT is copied; the visible label is the
        // state. A screen reader reading "Copy" four times in a transcript
        // cannot tell which block it is on.
        aria-label={state === 'copied' ? 'Code copied to clipboard' : 'Copy code to clipboard'}
        style={{
          position: 'absolute',
          top: '4px',
          right: '4px',
          padding: '2px 8px',
          fontSize: '0.75rem',
          fontFamily: 'inherit',
          lineHeight: 1.6,
          color: state === 'failed' ? '#92400e' : '#475569',
          background: '#fff',
          border: '1px solid #d0d7de',
          borderRadius: '5px',
          cursor: 'pointer',
        }}
      >
        {label}
      </button>
    </div>
  );
}

/**
 * Renders basic markdown: fenced code blocks (```lang\n…\n```) as a copyable
 * <pre> block, GFM pipe tables (chat-table-markdown), plus inline **bold**,
 * *italic*, `code`, links, and images.
 *
 * Fenced blocks are split out FIRST (the inline `code` regex would otherwise
 * mangle the triple backticks — e.g. the `embeddable.publish` <iframe> snippet
 * rendered as a stray "html" + loose text). Each non-fence run is then scanned
 * for tables; anything else keeps the original inline rendering, so existing chat
 * text is unchanged.
 *
 * `blocks` (company-page) opts into DOCUMENT mode: headings, bullet/numbered
 * lists and real paragraphs. Off by default so chat rendering is bit-identical.
 */
const MarkdownText = ({ text = '', onNavigate = null, blocks: docMode = false, chat = false }) => {
  if (!text) return null;

  // ```<optional-lang>\n <content> \n``` — content captured across newlines.
  const fence = /```[^\n]*\n?([\s\S]*?)```/g;
  const blocks = [];
  let lastIndex = 0;
  let m;
  while ((m = fence.exec(text)) !== null) {
    if (m.index > lastIndex) {
      blocks.push({ type: 'text', content: text.slice(lastIndex, m.index) });
    }
    blocks.push({ type: 'codeblock', content: m[1].replace(/\n$/, '') });
    lastIndex = fence.lastIndex;
  }
  if (lastIndex < text.length) {
    blocks.push({ type: 'text', content: text.slice(lastIndex) });
  }
  if (blocks.length === 0) {
    blocks.push({ type: 'text', content: text });
  }

  return (
    <>
      {blocks.map((b, i) =>
        b.type === 'codeblock' ? (
          <CodeBlock key={i} content={b.content} />
        ) : (
          <React.Fragment key={i}>
            {_parseTextBlock(b.content, docMode).map((seg, si) => {
              if (seg.type === 'table') return _renderTable(seg, `${i}-${si}`, onNavigate, chat);
              if (seg.type === 'text') {
                return (
                  <React.Fragment key={`${i}-${si}`}>
                    {renderInline(seg.content, `${i}-${si}`, onNavigate, chat)}
                  </React.Fragment>
                );
              }
              return _renderDocSegment(seg, `${i}-${si}`, onNavigate, chat);
            })}
          </React.Fragment>
        )
      )}
    </>
  );
};

export default MarkdownText;
